// 261005 E2-P2a — hook tải dữ liệu của khung Trợ lý. Mọi quyết định (poll, chọn luồng) ở `assistantView.ts`.
// - scope không hỗ trợ ⇒ 0 request. Đổi scope ⇒ GET scope + GET threads ⇒ pickThreadId ⇒ GET thread (≤3 request, 0 AI).
// - Y2: dedupe theo khoá `story|stageKey|scopeKey` (StrictMode chạy effect 2 lần ⇒ dùng chung MỘT lượt tải); kết quả về muộn của scope cũ bị bỏ.
// - Poll 3 s: GET thread khi `shouldPollThread`, GET scope khi `shouldPollScope`; có→không ⇒ `onSettled()` + tải lại phiên bản (nếu đã mở).
import { useCallback, useEffect, useRef, useState } from 'react';
import { adaptationAssistantApi } from '../../../../services/api/adaptationAssistant';
import type { AssistantStageKey, AssistantThread, AssistantVersion, ScopeParams, ScopeView, ThreadDetail } from '../../../../types/adaptationAssistant';
import { AssistantScope, isGoneCode, pickThreadId, shouldPollScope, shouldPollThread, shouldRefetchWhenGone } from './assistantView';

const POLL_MS = 3000;
/** 261015 P4a: kiểm nối ra "khớp" ⇒ HỆ THỐNG tự nối ngay sau (vài trăm ms, cùng khoá job); lần GET ngay khi thẻ rời RUNNING có thể rơi đúng khe đó (thẻ còn `AI_CONSISTENT`) ⇒ tải lại thêm MỘT lần sau chừng này. */
export const SETTLE_RECHECK_MS = 2500;

interface Loaded { view: ScopeView | null; threads: AssistantThread[]; threadId: string | null; detail: ThreadDetail | null }
const EMPTY: Loaded = { view: null, threads: [], threadId: null, detail: null };

const codeOf = (e: unknown): string | undefined => (e as { response?: { data?: { code?: string } } })?.response?.data?.code;

export interface AssistantScopeApi extends Loaded {
  loading: boolean;
  /** Lỗi tải khác 404-tập-mất (hiện cho người dùng). */
  loadError: unknown | null;
  /** Tập không còn / chặng không hỗ trợ ở server (R3) ⇒ hiện như "chưa hỗ trợ". */
  gone: boolean;
  versions: AssistantVersion[] | null;
  reloadScope: () => Promise<void>;
  reloadThread: (id?: string | null) => Promise<void>;
  reloadAll: () => Promise<void>;
  loadVersions: () => Promise<void>;
  selectThread: (id: string | null) => Promise<void>;
  newConversation: () => void;
  /** Luồng lười: chưa có luồng ⇒ POST threads. */
  ensureThread: () => Promise<string>;
}

/** `dataVersion` đổi khi khung đang `gone` (vd ① chưa có chương, vừa nạp xong) ⇒ tải lại từ đầu (F10). */
/** `reloadNonce` đổi (E4-P1: lưu sửa tay) ⇒ tải lại scope + luồng + phiên bản — KHÔNG tính lần đầu. */
export const useAssistantScope = (storyId: string, scope: AssistantScope, onSettled: () => void, reloadNonce = 0, dataVersion = 0): AssistantScopeApi => {
  const key = scope.supported ? `${storyId}|${scope.stageKey}|${scope.scopeKey}` : null;
  const params: ScopeParams | null = scope.supported ? { storyId, stageKey: scope.stageKey as AssistantStageKey, scopeKey: scope.scopeKey } : null;
  const paramsRef = useRef(params); paramsRef.current = params;
  const keyRef = useRef(key); keyRef.current = key;
  const onSettledRef = useRef(onSettled); onSettledRef.current = onSettled;

  const [data, setData] = useState<Loaded>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown | null>(null);
  const [gone, setGone] = useState(false);
  const [versions, setVersions] = useState<AssistantVersion[] | null>(null);
  const threadIdRef = useRef<string | null>(null); threadIdRef.current = data.threadId;
  const versionsLoaded = useRef(false);
  const inflight = useRef<{ key: string; promise: Promise<void> } | null>(null);

  /** Chỉ áp kết quả nếu scope lúc gọi còn là scope hiện tại. */
  const ifCurrent = (k: string | null, fn: () => void) => { if (k !== null && keyRef.current === k) fn(); };

  const handleError = (k: string | null, e: unknown) => ifCurrent(k, () => {
    const c = codeOf(e);
    if (isGoneCode(c)) { setGone(true); setData(EMPTY); } else setLoadError(e);
  });

  const reloadScope = useCallback(async () => {
    const p = paramsRef.current; const k = keyRef.current;
    if (!p) return;
    try { const view = await adaptationAssistantApi.scope(p); ifCurrent(k, () => setData((d) => ({ ...d, view }))); } catch (e) { handleError(k, e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadThread = useCallback(async (id?: string | null) => {
    const k = keyRef.current; const tid = id === undefined ? threadIdRef.current : id;
    if (!k || !tid) return;
    try { const detail = await adaptationAssistantApi.getThread(tid); ifCurrent(k, () => setData((d) => (d.threadId === tid ? { ...d, detail } : d))); } catch (e) { handleError(k, e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = useCallback(async (): Promise<void> => {
    const p = paramsRef.current; const k = keyRef.current;
    if (!p || !k) return;
    setLoading(true); setLoadError(null); setGone(false);
    try {
      const [view, threads] = await Promise.all([adaptationAssistantApi.scope(p), adaptationAssistantApi.listThreads(p)]);
      const threadId = pickThreadId(threads, threadIdRef.current);
      const detail = threadId ? await adaptationAssistantApi.getThread(threadId) : null;
      ifCurrent(k, () => setData({ view, threads, threadId, detail }));
    } catch (e) { handleError(k, e); } finally { ifCurrent(k, () => setLoading(false)); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Đổi scope ⇒ tải lại từ đầu. Cùng khoá (StrictMode chạy 2 lần) ⇒ dùng chung lượt đang có.
  useEffect(() => {
    threadIdRef.current = null; versionsLoaded.current = false;
    setData(EMPTY); setVersions(null); setLoadError(null); setGone(false); setLoading(!!key);
    if (!key) { inflight.current = null; return; }
    if (inflight.current?.key !== key) inflight.current = { key, promise: fetchAll() };
  }, [key, fetchAll]);

  const loadVersions = useCallback(async () => {
    const p = paramsRef.current; const k = keyRef.current;
    if (!p) return;
    try { const v = await adaptationAssistantApi.listVersions(p); versionsLoaded.current = true; ifCurrent(k, () => setVersions(v)); } catch (e) { handleError(k, e); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadAll = useCallback(async () => {
    await Promise.all([reloadScope(), reloadThread()]);
    if (versionsLoaded.current) await loadVersions();
  }, [reloadScope, reloadThread, loadVersions]);

  const lastNonce = useRef(reloadNonce);
  useEffect(() => {
    if (lastNonce.current === reloadNonce) return;
    lastNonce.current = reloadNonce;
    if (keyRef.current) reloadAll();
  }, [reloadNonce, reloadAll]);

  const lastDataVersion = useRef(dataVersion);
  useEffect(() => {
    const prev = lastDataVersion.current;
    lastDataVersion.current = dataVersion;
    if (shouldRefetchWhenGone(gone || loadError !== null, !!keyRef.current, prev, dataVersion)) { inflight.current = null; fetchAll(); }
  }, [dataVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectThread = useCallback(async (id: string | null) => {
    const k = keyRef.current;
    threadIdRef.current = id;
    setData((d) => ({ ...d, threadId: id, detail: null }));
    if (id) await reloadThread(id);
    else ifCurrent(k, () => undefined);
  }, [reloadThread]);

  const newConversation = useCallback(() => { threadIdRef.current = null; setData((d) => ({ ...d, threadId: null, detail: null })); }, []);

  const ensureThread = useCallback(async (): Promise<string> => {
    if (threadIdRef.current) return threadIdRef.current;
    const p = paramsRef.current; const k = keyRef.current;
    if (!p) throw new Error('Chưa có scope');
    const t = await adaptationAssistantApi.createThread(p);
    threadIdRef.current = t.id;
    ifCurrent(k, () => setData((d) => ({ ...d, threads: [t, ...d.threads.filter((x) => x.id !== t.id)], threadId: t.id, detail: null })));
    return t.id;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll.
  const pollThread = shouldPollThread(data.detail);
  const pollScope = shouldPollScope(data.view);
  useEffect(() => {
    if (!key || (!pollThread && !pollScope)) return undefined;
    const t = setInterval(() => {
      if (pollThread) reloadThread();
      if (pollScope) reloadScope();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [key, pollThread, pollScope, reloadThread, reloadScope]);

  // Có → không: làm mới dữ liệu chung + phiên bản .
  const wasPolling = useRef(false);
  const polling = pollThread || pollScope;
  useEffect(() => {
    if (wasPolling.current && !polling) {
      onSettledRef.current();
      reloadScope();
      if (versionsLoaded.current) loadVersions();
    }
    wasPolling.current = polling;
  }, [polling]); // eslint-disable-line react-hooks/exhaustive-deps

  // 261015 P4a: CHỈ khi thẻ nối tập rời `RUNNING` (`pollScope` true→false; mọi lượt poll thread/job khác KHÔNG chạm): kiểm nối ra "khớp" ⇒ hệ thống tự nối ngay sau;
  // lần tải lại ngay lúc settle có thể rơi đúng khe thẻ còn `AI_CONSISTENT` ⇒ tải lại thêm MỘT lần sau `SETTLE_RECHECK_MS`.
  const wasPollingScope = useRef(false);
  const recheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (wasPollingScope.current && !pollScope) {
      if (recheckTimer.current) clearTimeout(recheckTimer.current);
      recheckTimer.current = setTimeout(() => { recheckTimer.current = null; onSettledRef.current(); reloadScope(); }, SETTLE_RECHECK_MS);
    }
    wasPollingScope.current = pollScope;
  }, [pollScope]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (recheckTimer.current) clearTimeout(recheckTimer.current); }, []);

  return { ...data, loading, loadError, gone, versions, reloadScope, reloadThread, reloadAll, loadVersions, selectThread, newConversation, ensureThread };
};
